// Local Storage utility for data persistence

const STORAGE_KEYS = {
  LEADS: 'pocketops_leads',
  PRODUCT_DATA_SYSTEM: 'pocketops_product_data_system',
  PRODUCT_DATA_PERSONAL: 'pocketops_product_data_personal',
  CONNECTORS: 'pocketops_connectors',
  POSTS: 'pocketops_posts',
  SM_CONTACTS: 'pocketops_sm_contacts',
  BROADCAST_GROUPS: 'pocketops_broadcast_groups',
  BROADCAST_TEMPLATES: 'pocketops_broadcast_templates',
  PAYOUTS: 'pocketops_payouts',
  MONTHLY_COMMITMENT: 'pocketops_monthly_commitment',
  LEAD_STAGE_HISTORY: 'pocketops_lead_stage_history',
};

// Generic get/set with JSON parsing
export function getFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored, (key, value) => {
        // Convert date strings back to Date objects
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          return new Date(value);
        }
        return value;
      });
      return parsed;
    }
  } catch (e) {
    console.error('Error reading from localStorage:', e);
  }
  return defaultValue;
}

export function saveToStorage<T>(key: string, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error('Error saving to localStorage:', e);
  }
}

// Lead-specific functions
export function getLeads() {
  return getFromStorage(STORAGE_KEYS.LEADS, null);
}

export function saveLeads(leads: any[]) {
  saveToStorage(STORAGE_KEYS.LEADS, leads);
}

// Product data functions
export function getProductDataSystem() {
  return getFromStorage(STORAGE_KEYS.PRODUCT_DATA_SYSTEM, null);
}

export function saveProductDataSystem(data: any) {
  saveToStorage(STORAGE_KEYS.PRODUCT_DATA_SYSTEM, data);
}

export function getProductDataPersonal() {
  return getFromStorage(STORAGE_KEYS.PRODUCT_DATA_PERSONAL, null);
}

export function saveProductDataPersonal(data: any) {
  saveToStorage(STORAGE_KEYS.PRODUCT_DATA_PERSONAL, data);
}

// Connector functions
export function getConnectors() {
  return getFromStorage(STORAGE_KEYS.CONNECTORS, null);
}

export function saveConnectors(connectors: any[]) {
  saveToStorage(STORAGE_KEYS.CONNECTORS, connectors);
}

// Posts functions
export function getPosts() {
  return getFromStorage(STORAGE_KEYS.POSTS, null);
}

export function savePosts(posts: any[]) {
  saveToStorage(STORAGE_KEYS.POSTS, posts);
}

// Banker Contact functions
export function getSMContacts() {
  return getFromStorage(STORAGE_KEYS.SM_CONTACTS, null);
}

export function saveSMContacts(contacts: any[]) {
  saveToStorage(STORAGE_KEYS.SM_CONTACTS, contacts);
}

// Broadcast Group functions
export function getBroadcastGroups() {
  return getFromStorage(STORAGE_KEYS.BROADCAST_GROUPS, null);
}

export function saveBroadcastGroups(groups: any[]) {
  saveToStorage(STORAGE_KEYS.BROADCAST_GROUPS, groups);
}

// Broadcast Template functions
export function getBroadcastTemplates() {
  return getFromStorage(STORAGE_KEYS.BROADCAST_TEMPLATES, null);
}

export function saveBroadcastTemplates(templates: any[]) {
  saveToStorage(STORAGE_KEYS.BROADCAST_TEMPLATES, templates);
}

// Payouts functions
export function getPayouts() {
  return getFromStorage(STORAGE_KEYS.PAYOUTS, null);
}

export function savePayouts(payouts: any[]) {
  saveToStorage(STORAGE_KEYS.PAYOUTS, payouts);
}

// Monthly Commitment functions
export interface MonthlyCommitment {
  userId: string;
  loginFiles: number;
  loginAmount: number;
  disbursementFiles: number;
  disbursementAmount: number;
  submittedAt: string;
  month: string; // YYYY-MM format
}

// Get all commitments for all users
function getAllMonthlyCommitments(): MonthlyCommitment[] {
  return getFromStorage<MonthlyCommitment[]>(STORAGE_KEYS.MONTHLY_COMMITMENT, []);
}

// Save all commitments
function saveAllMonthlyCommitments(commitments: MonthlyCommitment[]) {
  saveToStorage(STORAGE_KEYS.MONTHLY_COMMITMENT, commitments);
}

// Get commitment for a specific user
export function getMonthlyCommitment(userId: string): MonthlyCommitment | null {
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const allCommitments = getAllMonthlyCommitments();
  const userCommitment = allCommitments.find(
    c => c.userId === userId && c.month === currentMonth
  );
  return userCommitment || null;
}

// Save commitment for a specific user
export function saveMonthlyCommitment(commitment: MonthlyCommitment) {
  const allCommitments = getAllMonthlyCommitments();
  const currentMonth = new Date().toISOString().slice(0, 7);
  
  // Remove any existing commitment for this user in this month
  const filteredCommitments = allCommitments.filter(
    c => !(c.userId === commitment.userId && c.month === currentMonth)
  );
  
  // Add the new commitment
  filteredCommitments.push(commitment);
  saveAllMonthlyCommitments(filteredCommitments);
}

// Check if user can edit their commitment
export function isMonthlyCommitmentEditable(userId: string): boolean {
  const today = new Date();
  const dayOfMonth = today.getDate();
  const existingCommitment = getMonthlyCommitment(userId);
  // Editable within first 5 days of month OR if not submitted yet
  return dayOfMonth <= 5 || !existingCommitment;
}

// Lead Stage History functions
export interface LeadStageChange {
  leadId: string;
  fromStatus: string;
  toStatus: string;
  changedAt: Date;
  changedBy: string;
}

export function getLeadStageHistory(): LeadStageChange[] {
  return getFromStorage<LeadStageChange[]>(STORAGE_KEYS.LEAD_STAGE_HISTORY, []);
}

export function saveLeadStageHistory(history: LeadStageChange[]) {
  saveToStorage(STORAGE_KEYS.LEAD_STAGE_HISTORY, history);
}

export function addLeadStageChange(change: LeadStageChange) {
  const history = getLeadStageHistory();
  history.push(change);
  saveLeadStageHistory(history);
}

export { STORAGE_KEYS };